export interface UploadedFile extends File {
  path?: string;
  preview?: string;
}

export interface DocumentType {
  formats: string[];
  toast: string;
}

export interface ValidatedFileFormat {
  isValid: boolean;
  errorMessage?: string;
  toastMessage?: string;
}

// Modular, configurable single source of truth for document types and their allowed formats.
const FORMAT_INFO: Record<string, DocumentType> = {
  Warranty: {
    formats: ['DOC', 'DOCX', 'PDF'],
    toast: 'DOC, DOCX, or PDF',
  },
  'Owners Manual': {
    formats: ['DOC', 'DOCX', 'PDF'],
    toast: 'DOC, DOCX, or PDF',
  },
  Image: {
    formats: ['JPG', 'JPEG', 'PNG', 'GIF'],
    toast: 'JPG, JPEG, PNG, or GIF',
  },
  Floorplan: {
    formats: ['JPG', 'JPEG', 'PNG', 'PDF'],
    toast: 'JPG, PNG, or PDF',
  },
  Invoice: {
    formats: ['PDF'],
    toast: 'PDF',
  },
  Spreadsheet: {
    formats: ['XLSX', 'XLS', 'CSV', 'SHEET', 'PPT', 'PPTX'],
    toast: 'XLSX, XLS, CSV, SHEET, PPT, or PPTX',
  },
  Contract: {
    formats: ['PDF', 'JPG', 'JPEG', 'PNG', 'DOC', 'DOCX'],
    toast: 'PDF, JPG, JPEG, PNG, DOC, or DOCX',
  },
  Video: {
    formats: ['MP4', 'MOV', 'AVI', 'WMV', 'WEBM'],
    toast: 'MP4, MOV, AVI, WMV, or WEBM',
  },
};

const checkFileFormat = (
  fileName: string,
  documentTypes?: string | string[]
): ValidatedFileFormat => {
  if (!documentTypes) {
    return { isValid: true };
  }

  const typesToCheck: string[] = Array.isArray(documentTypes)
    ? documentTypes
    : [documentTypes];

  const fileExtension: string = fileName.split('.').pop()?.toUpperCase() || '';

  // Check if file is valid for any of the specified types
  for (const type of typesToCheck) {
    const info: DocumentType = FORMAT_INFO[type];

    if (info?.formats.includes(fileExtension)) {
      return { isValid: true };
    }
  }

  // Build error messages
  const formatInfoList: DocumentType[] = typesToCheck
    .map((type: string) => FORMAT_INFO[type])
    .filter(
      (info: DocumentType): info is DocumentType =>
        info !== undefined && info.formats.length > 0
    );

  if (formatInfoList.length === 0) {
    return { isValid: true };
  }

  const allAllowedFormats: string[] = formatInfoList.flatMap(
    (info: DocumentType) => info.formats
  );
  const allAllowedFormatsForToast: string = formatInfoList
    .map((info: DocumentType) => info.toast)
    .join(', ');

  return {
    isValid: false,
    errorMessage: `You have uploaded an invalid file format, allowed formats are ${allAllowedFormats.join(', ')}`,
    toastMessage: `🚫 You have uploaded an invalid file format, allowed formats are ${allAllowedFormatsForToast}.`,
  };
};

export { checkFileFormat, FORMAT_INFO };
